import React, {useEffect} from 'react'
import {
  LeaseApplication,
  LeaseApplicationStatus,
  User
} from '../../types/app-types'
import {useCreateLeaseApplicationMutation} from '../../services/residentPortalApi'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useAppSelector} from '../../store/hooks'
import {selectUser} from '../../store/userSlice'
import Loader from '../common/Loader'
import Alert from '../common/Alert'
import {selectIsAuthenticated} from '../../store/authSlice'
import {parseJwtPayload} from '../../services/utils'

function createNewLeaseApplication(
  user: User,
  searchParams: URLSearchParams,
  createLeaseApplication: Function
): void {
  const token = searchParams.get('token')!
  const {email, firstName, lastName} = user

  if (!token) {
    const applicationType = searchParams.get('type')!
    const applicationPrimaryId = searchParams.get('primaryId')!
    const leasePropertyId = searchParams.get('propertyId')!
    const leaseUnitId = searchParams.get('unitId')!
    const leaseStartDate = searchParams.get('leaseStart')!
    const leaseEndDate = searchParams.get('leaseEnd')!
    createLeaseApplication({
      type: 'params',
      applicationType,
      applicationPrimaryId,
      applicantEmail: email,
      applicantFirstName: firstName,
      applicantLastName: lastName,
      applicationStatus: LeaseApplicationStatus.FormInProgress,
      leasePropertyId,
      leaseUnitId,
      leaseStartDate,
      leaseEndDate
    })
  } else {
    const payload = parseJwtPayload(token)
    createLeaseApplication({
      type: 'token',
      applicationType: payload.type,
      applicationPrimaryId: payload.primaryId,
      applicantEmail: email,
      applicantFirstName: firstName,
      applicantLastName: lastName,
      applicationStatus: LeaseApplicationStatus.FormInProgress,
      token
    })
  }
}

const NewLeaseApplicationPage = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [
    createLeaseApplication,
    createLeaseApplicationResult
  ] = useCreateLeaseApplicationMutation()

  // create lease application if not existing, set state
  useEffect(() => {
    // create a new lease application
    if (
      isAuthenticated &&
      user &&
      createLeaseApplicationResult.isUninitialized
    ) {
      createNewLeaseApplication(user, searchParams, createLeaseApplication)
    }

    // application created => redirect to lease application flow
    if (createLeaseApplicationResult.isSuccess) {
      const {id} = createLeaseApplicationResult.data as LeaseApplication
      navigate(`/lease-application/${id}`)
    }
  }, [isAuthenticated, user, createLeaseApplicationResult])

  return (
    <main>
      {createLeaseApplicationResult.isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <Loader text="Creating lease application..." />
        </div>
      ) : (
        ''
      )}

      {createLeaseApplicationResult.isError ? (
        <Alert type="error" className="p-8">
          An error occurred while creating a new lease application. If the
          problem persists, please contact support:
          <a className="link" target="_blank" href="mailto:hello@placemakr.com">
            hello@placemakr.com
          </a>
        </Alert>
      ) : (
        ''
      )}
    </main>
  )
}

export default NewLeaseApplicationPage
